import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, Brain, Activity, Zap, Stethoscope, FlaskConical, ScanLine,
  ShieldCheck, Languages, AlertTriangle, CheckCircle2, XCircle,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import AppointmentDialog from "@/components/AppointmentDialog";
import useSEO from "@/hooks/useSEO";

const SITE = "https://manasneuropsychiatryhospital.in";
const HERO = "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/5cd57190e7d3ffe585debdbc407fd2c67b435e8d4f1824710759b075924d92bd.jpeg";
const EEG = "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/b4f629092d5bf3f14ea8c2942185ce9411410fe8ca27416895e12e35bbccc54b.jpeg";

const differentiators = [
  {
    icon: ScanLine,
    title: "મગજની તપાસ પહેલાં, લેબલ પછી",
    desc: "માનસિક બીમારીનું નામ આપતાં પહેલાં અમે જોઈએ છીએ કે MRI, EEG કે લોહીની તપાસ કંઈ સમજાવી શકે છે કે નહીં — ગાંઠ, વાઈનું કેન્દ્ર, જૂનું લોહીનું ગઠ્ઠું કે શરીરની રોગપ્રતિકારક શક્તિનો હુમલો. તપાસ વગર અપાયેલું લેબલ માણસને દાયકાઓ સુધી વળગે છે.",
  },
  {
    icon: FlaskConical,
    title: "મટી શકે તેવાં કારણો પહેલાં શોધીએ છીએ",
    desc: "થાઇરોઇડની તકલીફ, વિટામિન B12ની ઊણપ, વિલ્સન રોગ, અનિયંત્રિત ડાયાબિટીસ, લીવર–કિડનીની નિષ્ફળતા અને સ્ટેરોઇડ જેવી દવાઓ — આ બધાં હતાશા, ઉન્માદ કે ભ્રમ પેદા કરી શકે છે. મૂળ કારણ મટાડી શકાય છે, પણ મોટેભાગે તે તપાસવામાં જ આવતું નથી.",
  },
  {
    icon: Stethoscope,
    title: "આ જ વિષયની ખાસ તાલીમ",
    desc: "ડૉ. કમલેશ એ. શાહ MD (ન્યુરોસાઇકિયાટ્રી) છે — મગજના રોગ જ્યારે માનસિક બીમારી તરીકે દેખાય ત્યારની આ જ શાખા છે. ૧૯૮૯થી આ હોસ્પિટલ ન્યુરોલોજી અને સાઇકિયાટ્રીના આ સંધિસ્થાને કામ કરે છે.",
  },
  {
    icon: Zap,
    title: "ન્યુરોમોડ્યુલેશન અહીં જ",
    desc: "જ્યારે મગજને ઈજા થઈ હોય કે શરીર નબળું હોવાથી દવા મર્યાદિત પડે, ત્યારે rTMS, tDCS અને mBPT ભાવનગરમાં જ ઉપલબ્ધ છે — બીજા શહેર મોકલવાની જરૂર નથી.",
  },
];

const conditionGroups = [
  {
    label: "વાઈ (ફીટ) સાથે જોડાયેલી તકલીફો",
    icon: Activity,
    items: [
      "વાઈ સાથે થતી હતાશા, ચિંતા અને ભ્રમ",
      "વાઈની દવાથી થતો સ્વભાવ અને મૂડનો ફેરફાર",
      "ફીટ પછીની ગૂંચવણ, આક્રમકતા અને યાદશક્તિની ખાલી જગ્યા",
      "ડિસોસિએટિવ (ફંક્શનલ) ફીટ — EEG સામાન્ય હોવા છતાં આવતા આંચકા",
      "ટેમ્પોરલ લોબ વાઈ — સ્વભાવ કે ધાર્મિક વર્તનમાં ફેરફાર રૂપે",
    ],
  },
  {
    label: "માથાની ઈજા પછી",
    icon: Brain,
    items: [
      "અકસ્માત પછી ચીડિયાપણું, ગુસ્સો અને સ્વભાવનો ફેરફાર",
      "યાદશક્તિ, ધ્યાન અને આયોજનશક્તિમાં ઘટાડો",
      "માથાનો દુખાવો, ચક્કર, ઊંઘ ન આવવી અને ઉદાસી (પોસ્ટ-કન્કશન)",
      "ઈજા પછીની હતાશા અને કોઈ કામમાં રસ ન રહેવો",
      "ઈજા પછી શરૂ થયેલી વાઈ સાથે વર્તનની તકલીફ",
    ],
  },
  {
    label: "લકવા (સ્ટ્રોક) પછી",
    icon: Activity,
    items: [
      "લકવા પછીની હતાશા — દર ત્રણમાંથી લગભગ એક દર્દીમાં",
      "ઉદાસીનતા, જેને પરિવાર આળસ કે જીદ સમજી લે છે",
      "કારણ વગર રડી પડવું કે હસી પડવું",
      "વિચાર–યાદશક્તિમાં ઘટાડો અને વાસ્ક્યુલર ડિમેન્શિયા",
      "ફરી લકવાના ડરથી કસરત–ફિઝિયોથેરાપી બંધ કરી દેવી",
    ],
  },
  {
    label: "પાર્કિન્સન અને કંપવા–હલનચલનના રોગ",
    icon: Brain,
    items: [
      "પાર્કિન્સનમાં હતાશા અને ચિંતા — ઘણીવાર કંપ શરૂ થતાં પહેલાં જ",
      "આગળના તબક્કામાં કે દવાને કારણે દેખાતાં દૃશ્ય–ભ્રમ",
      "દવાથી થતી આવેગની તકલીફ — જુગાર, બેફામ ખર્ચ, વધુ પડતી જાતીય ઇચ્છા",
      "યુવાન વયે વિલ્સન રોગ માનસિક બીમારી રૂપે દેખાવો",
      "કંપ, ટિક્સ અને ડિસ્ટોનિયા સાથેના વારંવારના વિચારો",
    ],
  },
  {
    label: "યાદશક્તિ, ગૂંચવણ અને બીમાર દર્દી",
    icon: Stethoscope,
    items: [
      "ડિમેન્શિયાનું નિદાન, તબક્કો અને સંભાળ રાખનારને માર્ગદર્શન",
      "ડિલિરિયમ — ICU, ઓપરેશન પછી અને વૃદ્ધ દર્દીઓમાં અચાનક ગૂંચવણ",
      "ડિમેન્શિયામાં શંકા, ભટકી જવું અને આક્રમકતા",
      "કેટાટોનિયા — ખાવાનું, બોલવાનું, હલનચલન બંધ કરી દેવું",
      "કેન્સર, ડાયાલિસિસ, HIV અને ICU પછીની માનસિક તકલીફો",
    ],
  },
  {
    label: "શારીરિક કારણ અને ફંક્શનલ તકલીફો",
    icon: FlaskConical,
    items: [
      "ઑટોઇમ્યૂન એન્સેફેલાઇટિસ — સાજા યુવાનમાં અચાનક 'પાગલપણું'",
      "મલ્ટિપલ સ્ક્લેરોસિસ સાથે હતાશા કે વિચારશક્તિમાં ફેરફાર",
      "થાઇરોઇડ, B12ની ઊણપ અને ક્ષારના અસંતુલનથી થતી માનસિક તકલીફ",
      "સ્ટેરોઇડ અને ટીબીની દવાથી થતો ભ્રમ",
      "ફંક્શનલ ન્યુરોલોજિકલ ડિસઓર્ડર — રિપોર્ટ સામાન્ય છતાં લકવો, અંધાપો કે કંપ",
    ],
  },
];

const redFlags = [
  "૪૫ વર્ષ પછી પહેલી જ વાર માનસિક લક્ષણો, અગાઉ કોઈ ઇતિહાસ નહીં",
  "સાજા-સારા માણસમાં થોડા દિવસોમાં જ અચાનક ફેરફાર",
  "સાથે તાવ, માથાનો દુખાવો, ફીટ કે ગરદન અકડાઈ જવી",
  "અવાજ સંભળાવા કરતાં દૃશ્યો દેખાવાં",
  "દિવસ દરમિયાન વધ-ઘટ થતી ગૂંચવણ, રાત્રે વધુ બગડે",
  "નબળાઈ, ખાલી ચડવી, ચાલવામાં તકલીફ, બેવડું દેખાવું કે પેશાબ પર કાબૂ ન રહેવો",
  "અઠવાડિયા–મહિનાઓમાં ઝડપથી ઘટતી યાદશક્તિ",
  "બે-બે પૂરતી દવાઓ છતાં જરાય ફરક ન પડવો",
];

const myths = [
  {
    myth: "ફીટ આવે એટલે માણસ પાગલ છે અથવા તેના પર કોઈ બહારની અસર છે.",
    fact:
      "વાઈ એ મગજમાં વીજપ્રવાહની અનિયમિતતાથી થતો ન્યુરોલોજિકલ રોગ છે — ભૂત-પ્રેત, કાળો જાદુ કે બુદ્ધિની ઊણપ નહીં. દુનિયાના વાઈના દર્દીઓમાંથી લગભગ છઠ્ઠો ભાગ ભારતમાં છે, છતાં મોટાભાગના દર્દી ડૉક્ટર પહેલાં ભુવા પાસે પહોંચે છે; કેટલાક વિસ્તારોમાં ૯૦%થી વધુ દર્દીઓને યોગ્ય સારવાર મળતી જ નથી. નિયમિત દવાથી લગભગ ૭૦% દર્દીઓની ફીટ સંપૂર્ણપણે બંધ થઈ જાય છે. દોરા-ધાગામાં ગુમાવેલો સમય જ આ રોગની સૌથી મોંઘી કિંમત છે.",
  },
  {
    myth: "MRI અને EEG સામાન્ય છે, એટલે ફીટ કે લકવો નાટક જ હશે.",
    fact:
      "ફંક્શનલ ન્યુરોલોજિકલ ડિસઓર્ડર સાચી બીમારી છે — મગજના નેટવર્કની ખરેખરી ખામી, જે દર્દીના કાબૂમાં નથી. સ્કેન અભ્યાસોમાં આવા દર્દીઓના મગજની પ્રવૃત્તિ નાટક કરનારા કરતાં સ્પષ્ટ રીતે અલગ દેખાય છે. તેનું નિદાન 'બીજું કંઈ ન મળ્યું' એમ કહીને નહીં, પણ ચોક્કસ તપાસનાં ચિહ્નોથી થાય છે, અને ન્યુરોલોજી OPDમાં આવતા લગભગ પાંચમાંથી એક દર્દીમાં તે જોવા મળે છે. સમજાવટ, ફિઝિયોથેરાપી અને મનોચિકિત્સાથી લગભગ અડધાથી વધુ દર્દીઓ એક વર્ષમાં નોંધપાત્ર સુધરે છે. 'નાટક કરે છે' કહેવું ખોટું પણ છે અને નુકસાનકારક પણ.",
  },
  {
    myth: "લકવા કે માથાની ઈજા પછી ઉદાસી તો સ્વાભાવિક છે, તેમાં કંઈ થાય નહીં.",
    fact:
      "લકવા પછીની હતાશા લગભગ ત્રીજા ભાગના દર્દીઓને થાય છે અને તે સંજોગો જેટલી જ મગજની ઈજાની સીધી અસર છે. સારવાર ન થાય તો શારીરિક રિકવરી ધીમી પડે છે, અપંગતા વધે છે અને મૃત્યુનું જોખમ પણ વધે છે. દવા અને થેરાપીથી તે સુધરે છે, અને તેની સારવારથી ફિઝિયોથેરાપીનું પરિણામ પણ સુધરે છે. 'સ્ટ્રોક આવ્યો છે એટલે ઉદાસ તો રહે જ' — આ જ વાક્ય દર્દીને વર્ષો સુધી સારવાર વગર રાખે છે.",
  },
  {
    myth: "ઉંમરે ભૂલી જવાય એ સામાન્ય છે, તપાસ કરાવવાની જરૂર નથી.",
    fact:
      "ઉંમર સાથે યાદ આવતાં વાર લાગે — પણ પૈસાનો હિસાબ, ઘરનો રસ્તો કે સ્વજનોને ઓળખવાનું ભુલાઈ જવું એ સામાન્ય ઘડપણ નથી. B12ની ઊણપ, થાઇરોઇડ, હતાશા, મગજમાં પાણી ભરાવું (NPH) અને માથામાં જૂનું લોહી જામવું — આ કારણો વહેલાં પકડાય તો યાદશક્તિ પાછી આવી શકે છે. અલ્ઝાઇમરમાં પણ વહેલું નિદાન વર્ષો સુધી સારી સ્થિતિ જાળવે છે અને પરિવારને આયોજનનો સમય આપે છે.",
  },
  {
    myth: "માનસિક બીમારીમાં સ્કેન કે લોહીની તપાસની જરૂર હોતી નથી.",
    fact:
      "મોટાભાગના કિસ્સામાં ખરેખર જરૂર નથી. પણ ૪૫ પછી પહેલી વારનાં લક્ષણો, થોડા દિવસોમાં અચાનક શરૂઆત, દૃશ્ય-ભ્રમ, તાવ, વધ-ઘટ થતી ગૂંચવણ કે કોઈ પણ નવું ન્યુરોલોજિકલ ચિહ્ન — આ બધું જવાબ સંપૂર્ણપણે બદલી નાખે છે. ઑટોઇમ્યૂન એન્સેફેલાઇટિસ, મગજની ગાંઠ, વિલ્સન રોગ અને થાઇરોઇડની તકલીફ — આ બધાંની મહિનાઓ સુધી 'સ્કિઝોફ્રેનિયા' કે 'બાયપોલર' તરીકે સારવાર થયાના દાખલા છે. તપાસ કરાવવી એ વધુ પડતી સાવચેતી નથી — એ કાયમી દવા અને સંપૂર્ણ સાજા થવા વચ્ચેનો ફરક છે.",
  },
  {
    myth: "પાર્કિન્સનના દર્દીમાં વર્તન બદલાય એટલે ડિમેન્શિયા શરૂ થઈ ગયું.",
    fact:
      "ડોપામિન એગોનિસ્ટ દવા લેતા દર્દીમાં જુગાર, બેફામ ખર્ચ, સતત ખાધા કરવું કે વધુ પડતી જાતીય ઇચ્છા દેખાય તો તે મોટેભાગે દવાની આડઅસર છે — ચારિત્ર્યનો દોષ કે ડિમેન્શિયા નહીં — અને દવા ગોઠવતાં જ તે શમી જાય છે. એ જ રીતે દૃશ્ય-ભ્રમ માટે પણ પહેલાં દવાની સમીક્ષા જરૂરી છે. આ લક્ષણો માટે પરિવાર જે અપરાધભાવ વર્ષો સુધી વેઠે છે, તે એક જ દવા-સમીક્ષાથી દૂર થઈ શકે છે.",
  },
];

const steps = [
  { t: "સંપૂર્ણ ઇતિહાસ — પરિવારની વાત સહિત", d: "શું બદલાયું, કયા ક્રમમાં અને કેટલા સમયમાં. જૂની દવાઓ, ડિસ્ચાર્જ કાર્ડ, સ્કેન અને EEG રિપોર્ટ ફરીથી કરાવવાને બદલે પહેલાં વાંચવામાં આવે છે. દર્દી જે કહી ન શકે તે પરિવાર જુએ છે." },
  { t: "ન્યુરોલોજિકલ અને માનસિક બંને તપાસ", d: "એક જ બેઠકમાં ચાલ, સ્નાયુની પકડ, રિફ્લેક્સ, આંખની હલનચલન અને વાણી સાથે મૂડ, વિચાર અને યાદશક્તિની ચકાસણી — અંદાજથી નહીં, નિયત પદ્ધતિથી." },
  { t: "જરૂર પૂરતી જ તપાસ", d: "MRI/CT, EEG, થાઇરોઇડ, B12, ક્ષાર, લીવર–કિડની, કોપર કે ઑટોઇમ્યૂન તપાસ — લક્ષણો જે તરફ ઇશારો કરે તે જ, આંધળી યાદી નહીં." },
  { t: "પહેલાં કારણ, પછી લક્ષણ", d: "ઊણપ પૂરી કરવી, વાઈ કાબૂમાં લેવી, નુકસાન કરતી દવા બંધ કરવી, જરૂર પડે તો ઇમ્યુનોથેરાપી માટે રિફર કરવું. માનસિક દવા પછી મગજના રોગ અને શરીરની બીજી બીમારીઓને ધ્યાનમાં રાખીને પસંદ થાય છે." },
  { t: "દવા મર્યાદિત પડે ત્યાં ન્યુરોમોડ્યુલેશન", d: "લકવા પછી, ઈજા પછી, વૃદ્ધ કે અનેક બીમારીવાળા દર્દીમાં rTMS, tDCS કે યોગ્ય કિસ્સામાં mBPT ઘણીવાર એ કરી બતાવે છે જે ગોળીઓ આડઅસર વગર કરી શકતી નથી." },
  { t: "પરિવારને તાલીમ અને લાંબો ફોલો-અપ", d: "દર્દી શેના પર કાબૂ રાખી શકે અને શેના પર નહીં, આક્રમકતા કે ભટકવા વખતે શું કરવું, અને ક્યારે તાત્કાલિક આવવું — આ બધું સમજાવાય છે. ફોલો-અપ નક્કી હોય છે, આગલી કટોકટી પર છોડાતો નથી." },
];

const faqs = [
  { q: "ન્યુરોસાઇકિયાટ્રી એટલે શું — સાઇકિયાટ્રી અને ન્યુરોલોજીથી અલગ શું?", a: "ન્યુરોલોજી મગજ અને જ્ઞાનતંતુના રોગોની સારવાર કરે છે; સાઇકિયાટ્રી મૂડ, વિચાર અને વર્તનની. ન્યુરોસાઇકિયાટ્રી આ બંનેનું સંધિસ્થાન છે — મગજના રોગથી સીધી ઊભી થતી માનસિક તકલીફો અને માનસિક બીમારી જેવી દેખાતી મગજની બીમારીઓ. વાઈ સાથેનો ભ્રમ, લકવા પછીની હતાશા, પાર્કિન્સનમાં દેખાતાં દૃશ્યો અને ઑટોઇમ્યૂન એન્સેફેલાઇટિસથી અચાનક બદલાયેલું વર્તન — આ બધાં અહીં આવે છે." },
  { q: "પહેલાં ન્યુરોલોજિસ્ટ પાસે જવું કે અહીં?", a: "જો મુખ્ય તકલીફ વર્તન, મૂડ, યાદશક્તિ, ગૂંચવણ કે ભ્રમની હોય તો પહેલાં અહીં આવો. અમે ન્યુરોલોજિકલ તપાસ કરીએ છીએ, જરૂરી હોય તો મગજની તપાસ કરાવીએ છીએ અને સ્કેન કે તપાસ માંગે ત્યારે ન્યુરોલોજિસ્ટ કે ન્યુરોસર્જન પાસે મોકલીએ છીએ — સાથે માનસિક સારવાર ચાલુ રાખીને. જો મુખ્ય તકલીફ ફક્ત નબળાઈ કે વારંવારની ફીટની હોય તો પહેલાં ન્યુરોલોજિસ્ટ યોગ્ય છે." },
  { q: "સ્વજનનું વર્તન અઠવાડિયામાં જ અચાનક બદલાઈ ગયું છે — આ ઇમરજન્સી છે?", a: "હા, જ્યાં સુધી સાબિત ન થાય ત્યાં સુધી તેને તબીબી ઇમરજન્સી જ ગણો. સાજા માણસમાં ખરેખર અચાનક ફેરફાર — ખાસ કરીને તાવ, માથાનો દુખાવો, ફીટ, વધ-ઘટ થતી ગૂંચવણ કે નવી નબળાઈ સાથે — એન્સેફેલાઇટિસ, મગજમાં લોહી, ડિલિરિયમ કે દવાની અસર તરફ ઇશારો કરે છે, પ્રાથમિક માનસિક બીમારી તરફ નહીં. તે જ દિવસે લઈ આવો. ઊંઘની ગોળી આપીને રાહ જોવી એ જ રીતે આ નિદાન ચૂકી જવાય છે." },
  { q: "અહીં EEG અને MRI થાય છે?", a: "EEG અને યાદશક્તિની તપાસ અમારા દર્દીઓ માટે ગોઠવવામાં આવે છે, અને MRI/CT ભાવનગરનાં જાણીતાં ઇમેજિંગ સેન્ટરોમાં કરાવાય છે — રિપોર્ટ પર જ આધાર રાખવાને બદલે ફિલ્મ અમે પોતે પણ જોઈએ છીએ. મહત્ત્વનું એ છે કે યોગ્ય કારણસર યોગ્ય તપાસ થાય અને તેને દર્દીની સ્થિતિ સાથે વાંચવામાં આવે." },
  { q: "વાઈના દર્દીને માનસિક રોગની દવા આપી શકાય?", a: "હા, પણ સાવચેતીથી. કેટલીક દવાઓ ફીટનું જોખમ વધારે છે અને ઘણી વાઈની દવા સાથે પ્રતિક્રિયા કરે છે. તેથી દવાની પસંદગી, ડોઝ અને સંયોજન વાઈને ધ્યાનમાં રાખીને નક્કી થાય છે. 'ફીટ છે એટલે માનસિક દવા ન લેવાય' એમ કહેવાથી મટી શકે તેવી હતાશા અને ભ્રમ સારવાર વગર રહી જાય છે — જવાબ છે સાચી દવા, દવા બંધ નહીં." },
  { q: "લકવા પછી પપ્પા આખો દિવસ બેસી રહે છે — હતાશા છે કે આળસ?", a: "બેમાંથી એકેય શબ્દ કદાચ બંધબેસતો નથી. લકવા પછીની ઉદાસીનતા (અપથી) મગજના ચોક્કસ ભાગને થયેલી ઈજાનું અલગ પરિણામ છે અને તે હતાશા સાથે કે તેના વગર પણ હોઈ શકે. તે ચારિત્ર્યની ખામી નથી અને ગમે તેટલું ઠપકારો તો પણ દર્દી 'જાતે મન મનાવી' શકતો નથી. તેની તપાસ અને સારવાર બંને શક્ય છે — અને ઘણીવાર તેની સારવાર પછી જ ફિઝિયોથેરાપી અસર કરવા લાગે છે." },
  { q: "બધા રિપોર્ટ સામાન્ય છે પણ તકલીફ સાચી છે. હવે શું?", a: "સામાન્ય રિપોર્ટ અમુક રોગોને બાકાત કરે છે — તેનો અર્થ એ નથી કે કંઈ નથી. ફંક્શનલ ન્યુરોલોજિકલ ડિસઓર્ડરનું નિદાન તપાસનાં ચોક્કસ ચિહ્નો પરથી થાય છે અને તે મગજના નેટવર્કની સાચી ખામી છે. સારવાર એટલે સ્પષ્ટ સમજાવટ, ફિઝિયોથેરાપી કે ઓક્યુપેશનલ થેરાપી અને મનોચિકિત્સા — જેનાથી મોટા ભાગના દર્દીઓ સુધરે છે. સૌથી ખરાબ પરિણામ 'કંઈ નથી' સાંભળીને વર્ષો વેડફવાથી આવે છે." },
  { q: "આ તકલીફો સંપૂર્ણપણે મટી જાય?", a: "તે સંપૂર્ણપણે કારણ પર આધાર રાખે છે. B12ની ઊણપ, દવાની આડઅસર, થાઇરોઇડ કે ડિલિરિયમ પૂરેપૂરાં મટી શકે છે. વાઈ સાથેની હતાશા સામાન્યપણે સારી રીતે કાબૂમાં આવે છે. લકવા કે માથાની ઈજા પછી ધ્યેય હોય છે મહત્તમ કાર્યક્ષમતા અને જીવનની ગુણવત્તા પાછી મેળવવી. તમારા સ્વજનના કિસ્સામાં શું શક્ય છે તે પહેલી જ મુલાકાતમાં સ્પષ્ટ કહેવામાં આવશે." },
  { q: "બાળકો અને યુવાનોને પણ જુઓ છો?", a: "હા — બાળપણની વાઈ સાથેની વર્તનની તકલીફ, મગજના તાવ પછીની અસરો, અકસ્માતમાં માથાની ઈજા, અને અચાનક ભ્રમ સાથે આવતો યુવાન જેમાં ઑટોઇમ્યૂન કે ચયાપચયનાં કારણો બાકાત કરવાં જરૂરી હોય. પદ્ધતિ એ જ રહે છે: કાયમી માનસિક લેબલ સ્વીકારતાં પહેલાં મગજની તપાસ." },
  { q: "હોસ્પિટલ ક્યાં છે અને એપોઇન્ટમેન્ટ જરૂરી છે?", a: "માનસ ન્યુરોસાઇકિયાટ્રી હોસ્પિટલ — બીજો માળ, કાર્લટન સ્ક્વેર, માધવદીપ કોમ્પ્લેક્સ પાસે, કાળાનાળા, ભાવનગર. OPD સોમથી શનિ, સવારે ૯થી રાત્રે ૮. સમય માટે +૯૧ ૯૪૨૭૫ ૬૦૧૦૧ પર ફોન કરો. સિહોર, પાલીતાણા, તળાજા, મહુવા, બોટાદ અને અમરેલીથી દર્દીઓ આવે છે — સાથે બધા જૂના રિપોર્ટ, સ્કેન અને દવાની ચિઠ્ઠી લેતા આવો." },
];

const JSONLD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalWebPage",
      "@id": `${SITE}/gu/neuropsychiatry`,
      url: `${SITE}/gu/neuropsychiatry`,
      name: "ભાવનગરમાં ન્યુરોસાઇકિયાટ્રી સારવાર (ગુજરાતી)",
      description:
        "ભાવનગરની માનસ હોસ્પિટલમાં ન્યુરોસાઇકિયાટ્રી OPD — વાઈ સાથેની માનસિક તકલીફ, લકવા અને માથાની ઈજા પછીનો સ્વભાવ ફેરફાર, પાર્કિન્સન, ડિમેન્શિયા, ડિલિરિયમ, ઑટોઇમ્યૂન એન્સેફેલાઇટિસ અને ફંક્શનલ ન્યુરોલોજિકલ ડિસઓર્ડર.",
      inLanguage: "gu-IN",
      specialty: "Psychiatry",
      primaryImageOfPage: { "@type": "ImageObject", url: HERO },
      about: [
        { "@type": "MedicalSpecialty", name: "Neuropsychiatry" },
        { "@type": "MedicalCondition", name: "Epilepsy with psychiatric comorbidity" },
        { "@type": "MedicalCondition", name: "Post-stroke depression" },
        { "@type": "MedicalCondition", name: "Functional neurological disorder" },
      ],
      publisher: { "@id": `${SITE}/#organization` },
      provider: { "@id": `${SITE}/#organization` },
    },
    {
      "@type": "FAQPage",
      inLanguage: "gu-IN",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};

export default function NeuropsychiatryGu() {
  useSEO({
    title: "ભાવનગરમાં ન્યુરોસાઇકિયાટ્રી — મગજના રોગ અને માનસિક તકલીફ",
    description:
      "ભાવનગરની માનસ હોસ્પિટલમાં ન્યુરોસાઇકિયાટ્રી OPD — વાઈ સાથેની હતાશા–ભ્રમ, લકવા પછીની ઉદાસી, માથાની ઈજા પછીનો ગુસ્સો, પાર્કિન્સન, ડિમેન્શિયા, અચાનક ગૂંચવણ અને રિપોર્ટ સામાન્ય છતાં રહેતી તકલીફો. MD ન્યુરોસાઇકિયાટ્રી, ૧૯૮૯થી. ફોન +૯૧ ૯૪૨૭૫ ૬૦૧૦૧.",
    image: HERO,
    jsonLd: JSONLD,
    lang: "gu",
    alternates: [
      { hreflang: "en-IN", href: `${SITE}/neuropsychiatry` },
      { hreflang: "gu-IN", href: `${SITE}/gu/neuropsychiatry` },
      { hreflang: "x-default", href: `${SITE}/neuropsychiatry` },
    ],
  });
  const [open, setOpen] = useState(false);

  return (
    <div data-testid="neuropsychiatry-gu-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-16">
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <p className="eyebrow text-[#A39171]">ન્યુરોસાઇકિયાટ્રી · ૧૯૮૯થી</p>
          <Link
            to="/neuropsychiatry"
            data-testid="np-gu-switch-english"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#A39171]/40 text-[#5C524E] hover:border-[#E07A5F] hover:text-[#E07A5F] transition-colors"
          >
            <Languages className="w-3.5 h-3.5" /> Read in English
          </Link>
        </div>
        <h1 className="font-display text-4xl md:text-6xl text-[#2D2623] leading-[1.1] tracking-tight max-w-4xl">
          બીમારી ક્યારેક મનમાં નહીં, <br />
          <span className="italic text-[#E07A5F]">મગજમાં હોય છે</span>.
        </h1>
        <p className="mt-8 text-lg md:text-xl text-[#5C524E] max-w-3xl leading-relaxed">
          વાઈ સાથે હતાશા. માથાની ઈજા પછી ફાટી નીકળતો ગુસ્સો. લકવા પછી ચૂપચાપ બેસી રહેતા પિતા. ચાર જ
          દિવસમાં ભ્રમમાં સરી પડેલી યુવતી. આ સ્વભાવની કે શ્રદ્ધાની ખામી નથી — આ મગજની બીમારીઓ છે, અને
          એટલે જ આ હોસ્પિટલના નામમાં 'ન્યુરોસાઇકિયાટ્રી' શબ્દ છે.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <Button data-testid="np-gu-hero-book" onClick={() => setOpen(true)} className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] px-8 h-14 font-medium">
            સમય માંગો <ArrowUpRight className="ml-1 w-4 h-4" />
          </Button>
          <a href="tel:+919427560101" data-testid="np-gu-hero-call" className="rounded-full border border-[#2D2623]/20 hover:border-[#E07A5F] hover:text-[#E07A5F] text-[#2D2623] px-8 h-14 inline-flex items-center justify-center font-medium transition-colors">
            ફોન +૯૧ ૯૪૨૭૫ ૬૦૧૦૧
          </a>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 mb-24">
        <div className="rounded-3xl overflow-hidden aspect-[16/9] md:aspect-[21/9] relative">
          <img src={HERO} alt="માનસ હોસ્પિટલ, ભાવનગરમાં દર્દી અને પરિવાર સાથે MRI જોતા ડૉક્ટર" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/90 via-[#1A1513]/45 to-transparent" />
          <div className="absolute bottom-8 left-8 right-8 md:left-12 md:right-auto md:max-w-xl">
            <p className="eyebrow text-[#F0CF5B] mb-3 drop-shadow-[0_2px_8px_rgba(0,0,0,0.65)]">MD ન્યુરોસાઇકિયાટ્રી · કાળાનાળા, ભાવનગર</p>
            <p className="font-display text-2xl md:text-3xl leading-snug text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.7)]">
              રિપોર્ટ દર્દીની હાજરીમાં વંચાય છે — લેબલ ચોંટાડીને નહીં.
            </p>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">સામાન્ય દવાખાનામાં જે ન થઈ શકે</p>
          <h2 className="font-display text-3xl md:text-5xl text-[#2D2623] tracking-tight mb-6 max-w-3xl leading-snug">
            માનસિક બીમારીનું લેબલ <span className="italic text-[#E07A5F]">છેલ્લું પગલું હોવું જોઈએ, પહેલું નહીં</span>.
          </h2>
          <p className="text-lg text-[#5C524E] max-w-3xl leading-relaxed mb-14">
            મગજની ગાંઠ, ઑટોઇમ્યૂન એન્સેફેલાઇટિસ, વિલ્સન રોગ, થાઇરોઇડની તકલીફ અને વિટામિનની ઊણપ —
            આ બધાંની મહિનાઓ સુધી 'સ્કિઝોફ્રેનિયા' કે 'બાયપોલર' તરીકે સારવાર થયાના દાખલા છે. ફરક નસીબનો
            નથી — ફરક એટલો જ છે કે દવા લખતાં પહેલાં કોઈએ મગજ તપાસ્યું કે નહીં.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {differentiators.map((d, i) => (
              <div key={d.title} data-testid={`np-gu-diff-${i}`}
                className="bg-[#F4EFE6] rounded-3xl p-8 md:p-10 border border-[#A39171]/15 hover:border-[#E07A5F]/40 transition-all">
                <span className="w-12 h-12 rounded-2xl bg-[#E07A5F] text-white flex items-center justify-center mb-5">
                  <d.icon className="w-5 h-5" />
                </span>
                <h3 className="font-display text-2xl md:text-3xl text-[#2D2623] tracking-tight leading-snug">{d.title}</h3>
                <p className="text-[#5C524E] mt-4 leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONDITIONS */}
      <section className="bg-[#F4EFE6]/60 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">અમે શું સારવાર કરીએ છીએ</p>
          <h2 className="font-display text-3xl md:text-5xl text-[#2D2623] tracking-tight mb-14 max-w-3xl leading-snug">
            બે શાખાઓની <span className="italic text-[#E07A5F]">વચ્ચે આવતી તકલીફો</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {conditionGroups.map((g, i) => (
              <div key={g.label} data-testid={`np-gu-group-${i}`}
                className="bg-[#FDFBF7] rounded-3xl p-8 border border-[#A39171]/15 hover:shadow-[0_24px_60px_rgba(163,145,113,0.12)] transition-all">
                <g.icon className="w-7 h-7 text-[#E07A5F] mb-5" />
                <h3 className="font-display text-xl md:text-2xl text-[#2D2623] tracking-tight mb-5 leading-snug">{g.label}</h3>
                <ul className="space-y-3">
                  {g.items.map((it) => (
                    <li key={it} className="flex items-start gap-3 text-[#5C524E] text-sm leading-relaxed">
                      <CheckCircle2 className="w-4 h-4 text-[#E07A5F] mt-0.5 shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/conditions/epilepsy-depression" data-testid="np-gu-link-epilepsy" className="rounded-full border border-[#2D2623]/20 hover:border-[#E07A5F] hover:text-[#E07A5F] px-6 py-3 text-sm font-medium transition-colors">
              વાઈ સાથે હતાશા — વિગતવાર (અંગ્રેજી) <ArrowUpRight className="w-3.5 h-3.5 inline ml-1" />
            </Link>
            <Link to="/conditions/post-stroke-depression" data-testid="np-gu-link-poststroke" className="rounded-full border border-[#2D2623]/20 hover:border-[#E07A5F] hover:text-[#E07A5F] px-6 py-3 text-sm font-medium transition-colors">
              લકવા પછીની હતાશા — વિગતવાર (અંગ્રેજી) <ArrowUpRight className="w-3.5 h-3.5 inline ml-1" />
            </Link>
            <Link to="/gu/general-psychiatry" data-testid="np-gu-link-gp" className="rounded-full border border-[#2D2623]/20 hover:border-[#E07A5F] hover:text-[#E07A5F] px-6 py-3 text-sm font-medium transition-colors">
              સામાન્ય માનસિક રોગો (ગુજરાતી) <ArrowUpRight className="w-3.5 h-3.5 inline ml-1" />
            </Link>
            <Link to="/gu/neuromodulation" data-testid="np-gu-link-nm" className="rounded-full border border-[#2D2623]/20 hover:border-[#E07A5F] hover:text-[#E07A5F] px-6 py-3 text-sm font-medium transition-colors">
              rTMS · ન્યુરોમોડ્યુલેશન (ગુજરાતી) <ArrowUpRight className="w-3.5 h-3.5 inline ml-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* RED FLAGS */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="eyebrow text-[#A39171] mb-4">ચેતવણીનાં ચિહ્નો</p>
            <h2 className="font-display text-3xl md:text-5xl text-[#2D2623] tracking-tight leading-snug">
              આ લક્ષણો હોય ત્યારે <span className="italic text-[#E07A5F]">મગજની તપાસ જરૂરી છે</span>.
            </h2>
            <p className="text-[#5C524E] mt-6 leading-relaxed">
              મોટાભાગની માનસિક બીમારીમાં સ્કેનની જરૂર પડતી નથી. પણ નીચેનામાંથી કંઈ પણ હોય તો દવા શરૂ કરતાં
              પહેલાં તપાસ થવી જોઈએ — આગલા ફોલો-અપની રાહ જોયા વગર દર્દીને લઈ આવો.
            </p>
            <div className="mt-8 rounded-3xl overflow-hidden aspect-[4/3]">
              <img src={EEG} alt="માનસ ન્યુરોસાઇકિયાટ્રી હોસ્પિટલ, ભાવનગરમાં EEG તપાસ" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="bg-[#2D2623] text-[#FDFBF7] rounded-3xl p-8 md:p-10 relative overflow-hidden" data-testid="np-gu-red-flags">
              <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
              <span className="w-12 h-12 rounded-2xl bg-[#E07A5F] flex items-center justify-center mb-6 relative">
                <AlertTriangle className="w-5 h-5" />
              </span>
              <ul className="space-y-4 relative">
                {redFlags.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-[#FDFBF7]/90">
                    <XCircle className="w-4 h-4 text-[#E2C044] mt-1 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-sm text-[#FDFBF7]/60 relative">
                તાવ, માથાનો દુખાવો કે ફીટ સાથે અચાનક ગૂંચવણ એ તબીબી ઇમરજન્સી છે. તે જ દિવસે આવો અથવા
                +૯૧ ૯૪૨૭૫ ૬૦૧૦૧ પર ફોન કરો.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MYTHS */}
      <section className="bg-[#F4EFE6]/60 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">ગેરસમજ અને હકીકત</p>
          <h2 className="font-display text-3xl md:text-5xl text-[#2D2623] tracking-tight mb-14 max-w-3xl leading-snug">
            લોકો શું કહે છે, <span className="italic text-[#E07A5F]">અને સાચું શું છે</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {myths.map((m, i) => (
              <div key={m.myth} data-testid={`np-gu-myth-${i}`}
                className="bg-[#FDFBF7] rounded-3xl p-8 border border-[#A39171]/15">
                <div className="flex items-start gap-3 mb-5 pb-5 border-b border-[#A39171]/15">
                  <XCircle className="w-5 h-5 text-[#A39171] mt-1 shrink-0" />
                  <p className="font-display text-lg md:text-xl text-[#A39171] leading-snug">{m.myth}</p>
                </div>
                <div className="flex items-start gap-3">
                  <ShieldCheck className="w-5 h-5 text-[#E07A5F] mt-1 shrink-0" />
                  <p className="text-[#2D2623] leading-relaxed">{m.fact}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">અમારી પદ્ધતિ</p>
          <h2 className="font-display text-3xl md:text-5xl text-[#2D2623] tracking-tight mb-14 leading-snug">
            એક કેસ ખરેખર <span className="italic text-[#E07A5F]">કેવી રીતે તપાસાય છે</span>.
          </h2>
          <ol className="space-y-10">
            {steps.map((s, i) => (
              <li key={s.t} data-testid={`np-gu-step-${i}`} className="grid grid-cols-12 gap-6 border-b border-[#A39171]/15 pb-10 last:border-none">
                <div className="col-span-12 md:col-span-3"><span className="font-display text-4xl text-[#E07A5F]">0{i + 1}</span></div>
                <div className="col-span-12 md:col-span-9">
                  <h3 className="font-display text-xl md:text-3xl text-[#2D2623] tracking-tight leading-snug">{s.t}</h3>
                  <p className="text-[#5C524E] mt-3 text-base md:text-lg leading-relaxed">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#F4EFE6]/60 py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">વારંવાર પૂછાતા પ્રશ્નો</p>
          <h2 className="font-display text-3xl md:text-5xl text-[#2D2623] tracking-tight mb-14 leading-snug">
            પહેલી મુલાકાતમાં પરિવાર <span className="italic text-[#E07A5F]">આ જ પૂછે છે</span>.
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`npgu-${i}`} data-testid={`np-gu-faq-${i}`}
                className="bg-[#FDFBF7] rounded-2xl border border-[#A39171]/15 px-6 data-[state=open]:border-[#E07A5F]/35 transition-colors">
                <AccordionTrigger className="text-left font-display text-lg md:text-2xl text-[#2D2623] tracking-tight hover:no-underline py-5 leading-snug">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#5C524E] text-base md:text-lg leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#2D2623] text-[#FDFBF7] py-20 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center relative">
          <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-snug">
            જૂના રિપોર્ટ સાથે લાવો. <span className="italic text-[#E07A5F]">અમે તે બરાબર વાંચીશું</span>.
          </h2>
          <p className="text-[#FDFBF7]/70 mt-5 max-w-xl mx-auto">
            સ્કેન, EEG, ડિસ્ચાર્જ કાર્ડ અને અત્યાર સુધી અજમાવેલી દરેક દવા — જવાબ ઘણીવાર એ કાગળોમાં જ હોય છે
            જે કોઈએ સાથે બેસીને વાંચ્યા નથી.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Button data-testid="np-gu-cta-book" onClick={() => setOpen(true)} className="rounded-full bg-[#E07A5F] hover:bg-[#FDFBF7] hover:text-[#2D2623] text-white px-8 h-12 font-medium">સમય માંગો</Button>
            <a href="tel:+919427560101" className="rounded-full border border-[#FDFBF7]/30 hover:bg-[#FDFBF7] hover:text-[#2D2623] px-8 h-12 inline-flex items-center justify-center font-medium transition-colors">ફોન +૯૧ ૯૪૨૭૫ ૬૦૧૦૧</a>
          </div>
        </div>
      </section>

      <AppointmentDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
